import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Autocomplete,
  FormControlLabel,
  Switch,
  Slider,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { useAuth } from '../../hooks/useAuth';
import { scadaUtils } from '../../services/scadaService';
import { useSiteContext } from '../../hooks/useSiteContext';
import { useTheme } from '@mui/material/styles';

import {
  SCADATag,
  HistorianQueryParams,
  HistorianResponse,
  HistorianTagData,
  HistorianDataPoint,
} from '../../types/scada';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  annotationPlugin
);

// Available resolutions for data aggregation
const RESOLUTION_OPTIONS = [
  { value: 'auto', label: 'Auto', description: 'Automatically select best resolution' },
  { value: 'raw', label: 'Raw Data', description: 'All original data points' },
  { value: '1min', label: '1 Minute', description: 'Aggregated per minute' },
  { value: '5min', label: '5 Minutes', description: 'Aggregated per 5 minutes' },
  { value: '15min', label: '15 Minutes', description: 'Aggregated per 15 minutes' },
  { value: '1hour', label: '1 Hour', description: 'Aggregated per hour' },
  { value: '4hour', label: '4 Hours', description: 'Aggregated per 4 hours' },
  { value: '1day', label: '1 Day', description: 'Aggregated per day' },
] as const;

// Chart type options
const CHART_TYPE_OPTIONS = [
  { value: 'line', label: 'Line Chart' },
  { value: 'scatter', label: 'Scatter Plot' },
];

// Y-axis scaling options
const Y_AXIS_OPTIONS = [
  { value: 'auto', label: 'Auto Scale' },
  { value: 'zero', label: 'Start from Zero' },
  { value: 'custom', label: 'Custom Range' },
];

const SCADAHistorian: React.FC = () => {
  const { user } = useAuth();
  const { currentSite } = useSiteContext();
  const theme = useTheme();

     // State for historian configuration
   const [selectedTags, setSelectedTags] = useState<SCADATag[]>([]);
   const [availableTags, setAvailableTags] = useState<SCADATag[]>([]);
   const [startTime, setStartTime] = useState<Date>(new Date(Date.now() - 24 * 60 * 60 * 1000)); // 24 hours ago
   const [endTime, setEndTime] = useState<Date>(new Date());
   const [resolution, setResolution] = useState<'auto' | 'raw' | '1min' | '5min' | '15min' | '1hour' | '4hour' | '1day'>('5min');
   const [maxPoints, setMaxPoints] = useState<number>(1000);
   const [chartType, setChartType] = useState<'line' | 'scatter'>('line');

   // Advanced axis configuration
   interface AxisConfig {
     id: string;
     unit: string;
     scaling: 'auto' | 'zero' | 'custom';
     min?: number;
     max?: number;
     position: 'left' | 'right';
     color: string;
   }
   const [axisConfigs, setAxisConfigs] = useState<Map<string, AxisConfig>>(new Map());

     // State for data and UI
   const [historianData, setHistorianData] = useState<HistorianResponse | null>(null);
   const [loading, setLoading] = useState<boolean>(false);
   const [error, setError] = useState<string | null>(null);
   const [tagsLoading, setTagsLoading] = useState<boolean>(false);
   
       // Timeline overview state
    const [showTimelineOverview, setShowTimelineOverview] = useState<boolean>(true);
    const [timelineData, setTimelineData] = useState<HistorianResponse | null>(null);
    const [viewStartTime, setViewStartTime] = useState<Date>(new Date(Date.now() - 24 * 60 * 60 * 1000));
    const [viewEndTime, setViewEndTime] = useState<Date>(new Date());
    
         // Timeline drag state
     const [isDragging, setIsDragging] = useState<boolean>(false);
     const [isResizing, setIsResizing] = useState<boolean>(false);
     const [resizeHandle, setResizeHandle] = useState<'left' | 'right' | null>(null);
     const [dragStartX, setDragStartX] = useState<number>(0);
     const [dragStartViewStart, setDragStartViewStart] = useState<Date>(new Date());
     const [dragStartViewEnd, setDragStartViewEnd] = useState<Date>(new Date());
     const [isUserInteracting, setIsUserInteracting] = useState<boolean>(false);

           // Load available tags on component mount
    useEffect(() => {
      loadAvailableTags();
    }, [currentSite.siteId]);

    // Auto-refresh main chart when view time range changes (from timeline navigation)
    useEffect(() => {
      // Don't auto-refresh during user interactions (dragging/resizing)
      if (isUserInteracting) {
        return;
      }
      
      if (selectedTags.length > 0 && historianData) {
        // Only auto-refresh if we have data and the view times are different from the current data range
        const currentDataStart = new Date(historianData.query_info.start_time).getTime();
        const currentDataEnd = new Date(historianData.query_info.end_time).getTime();
        const newViewStart = viewStartTime.getTime();
        const newViewEnd = viewEndTime.getTime();
        
        // Check if view range is significantly different (more than 1 minute difference)
        const timeDiffThreshold = 60 * 1000; // 1 minute
        const startDiff = Math.abs(newViewStart - currentDataStart);
        const endDiff = Math.abs(newViewEnd - currentDataEnd);
        
        if (startDiff > timeDiffThreshold || endDiff > timeDiffThreshold) {
          console.log('🔄 View time range changed, refreshing main chart data...', {
            oldRange: `${new Date(currentDataStart).toISOString()} to ${new Date(currentDataEnd).toISOString()}`,
            newRange: `${viewStartTime.toISOString()} to ${viewEndTime.toISOString()}`
          });
          
          // Debounce rapid changes during dragging
          const refreshTimeout = setTimeout(() => {
            queryHistorianData();
          }, 300);
          
          return () => clearTimeout(refreshTimeout);
        }
      }
    }, [viewStartTime, viewEndTime, selectedTags, historianData, isUserInteracting]);

       // Handle drag/resize event listeners at component level
    useEffect(() => {
      let updateTimeout: NodeJS.Timeout;
      
             const handleMouseMove = (e: MouseEvent) => {
         const rect = document.querySelector('[data-timeline-container]')?.getBoundingClientRect();
         if (!rect) return;
         
         // Use actual timeline data bounds instead of calculated bounds
         let timelineStart: number, timelineEnd: number;
         if (timelineData && timelineData.data.length > 0) {
           const timelineDataPoints = timelineData.data[0]?.data_points || [];
           if (timelineDataPoints.length > 0) {
             timelineStart = new Date(timelineDataPoints[0].timestamp).getTime();
             timelineEnd = new Date(timelineDataPoints[timelineDataPoints.length - 1].timestamp).getTime();
           } else {
             // Fallback to calculated bounds
             const selectedDuration = endTime.getTime() - startTime.getTime();
             const days = selectedDuration / (1000 * 60 * 60 * 24);
             let extensionMultiplier = days <= 1 ? 5.0 : days <= 7 ? 3.0 : days <= 30 ? 2.0 : days <= 90 ? 1.5 : 1.0;
             const extension = selectedDuration * extensionMultiplier;
             timelineStart = startTime.getTime() - extension;
             timelineEnd = endTime.getTime() + extension;
           }
         } else {
           // Fallback when no timeline data
           const selectedDuration = endTime.getTime() - startTime.getTime();
           const days = selectedDuration / (1000 * 60 * 60 * 24);
           let extensionMultiplier = days <= 1 ? 5.0 : days <= 7 ? 3.0 : days <= 30 ? 2.0 : days <= 90 ? 1.5 : 1.0;
           const extension = selectedDuration * extensionMultiplier;
           timelineStart = startTime.getTime() - extension;
           timelineEnd = endTime.getTime() + extension;
         }
         
         const timelineTotal = timelineEnd - timelineStart;
        
        const deltaX = e.clientX - dragStartX;
        const deltaPercent = deltaX / rect.width;
        const deltaTime = deltaPercent * timelineTotal;
        
        let shouldUpdate = false;
        
        if (isDragging) {
          // Drag to pan
          const newStart = new Date(dragStartViewStart.getTime() + deltaTime);
          const newEnd = new Date(dragStartViewEnd.getTime() + deltaTime);
          
          // Constrain to timeline bounds
          const duration = newEnd.getTime() - newStart.getTime();
          const minStart = timelineStart;
          const maxEnd = timelineEnd;
          
          if (newStart.getTime() < minStart) {
            setViewStartTime(new Date(minStart));
            setViewEndTime(new Date(minStart + duration));
          } else if (newEnd.getTime() > maxEnd) {
            setViewEndTime(new Date(maxEnd));
            setViewStartTime(new Date(maxEnd - duration));
          } else {
            setViewStartTime(newStart);
            setViewEndTime(newEnd);
          }
          shouldUpdate = true;
        } else if (isResizing && resizeHandle) {
          // Resize to zoom
          const minDuration = 60 * 1000; // Minimum 1 minute window
          
          if (resizeHandle === 'left') {
            const newStart = new Date(dragStartViewStart.getTime() + deltaTime);
            const newDuration = dragStartViewEnd.getTime() - newStart.getTime();
            
            if (newDuration >= minDuration && newStart.getTime() >= timelineStart) {
              setViewStartTime(newStart);
              shouldUpdate = true;
            }
          } else if (resizeHandle === 'right') {
            const newEnd = new Date(dragStartViewEnd.getTime() + deltaTime);
            const newDuration = newEnd.getTime() - dragStartViewStart.getTime();
            
            if (newDuration >= minDuration && newEnd.getTime() <= timelineEnd) {
              setViewEndTime(newEnd);
              shouldUpdate = true;
            }
          }
        }
        
                                   // Only update visual position during drag/resize, no data fetching
          if (shouldUpdate) {
            // Visual updates only - data will be fetched when user finishes interaction
            console.log('🔄 Updating view position during interaction...');
          }
      };
      
             const handleMouseUp = () => {
         if (isDragging || isResizing) {
           setIsDragging(false);
           setIsResizing(false);
           setResizeHandle(null);
           // Clear any pending updates
           clearTimeout(updateTimeout);
           
           // Clear interaction state and trigger data fetch after user finishes
           setIsUserInteracting(false);
           
           // Trigger data fetch for the new view range
           if (selectedTags.length > 0) {
             console.log('🔄 User finished interaction, fetching data for new view range...');
             setTimeout(() => {
               queryHistorianData();
             }, 100);
           }
         }
       };

      if (isDragging || isResizing) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          clearTimeout(updateTimeout);
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
      
      return () => clearTimeout(updateTimeout);
         }, [isDragging, isResizing, dragStartX, dragStartViewStart, dragStartViewEnd, resizeHandle, startTime, endTime, timelineData]);

  const loadAvailableTags = async () => {
    setTagsLoading(true);
    try {
      const tags = await scadaUtils.getAvailableTags(currentSite.siteId || undefined);
      setAvailableTags(tags);
    } catch (err) {
      console.error('Failed to load SCADA tags:', err);
      setError('Failed to load available SCADA tags');
    } finally {
      setTagsLoading(false);
    }
  };

     // Auto-generate axis configurations based on selected tags
   const generateAxisConfigs = useCallback((tags: SCADATag[]) => {
     const newAxisConfigs = new Map<string, AxisConfig>();
     const unitGroups = new Map<string, SCADATag[]>();
     
     // Group tags by engineering unit
     tags.forEach(tag => {
       const unit = tag.engineering_unit || 'N/A';
       if (!unitGroups.has(unit)) {
         unitGroups.set(unit, []);
       }
       unitGroups.get(unit)!.push(tag);
     });

     // Generate axis configuration for each unit
     const axisColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2'];
     Array.from(unitGroups.keys()).forEach((unit, index) => {
       const axisId = index === 0 ? 'y' : `y${index}`;
       const existingConfig = axisConfigs.get(axisId);
       
       newAxisConfigs.set(axisId, {
         id: axisId,
         unit: unit,
         scaling: existingConfig?.scaling || 'auto',
         min: existingConfig?.min,
         max: existingConfig?.max,
         position: index % 2 === 0 ? 'left' : 'right',
         color: axisColors[index % axisColors.length],
       });
     });

     setAxisConfigs(newAxisConfigs);
   }, [axisConfigs]);

               const handleTagChange = (event: any, newValue: SCADATag[]) => {
       setSelectedTags(newValue);
       generateAxisConfigs(newValue);
       // Initialize view times to match query times
       setViewStartTime(startTime);
       setViewEndTime(endTime);
       // Reset timeline data to force reload
       setTimelineData(null);
     };

  const handleResolutionChange = (event: SelectChangeEvent) => {
    setResolution(event.target.value as 'auto' | 'raw' | '1min' | '5min' | '15min' | '1hour' | '4hour' | '1day');
  };

     const handleChartTypeChange = (event: SelectChangeEvent) => {
     setChartType(event.target.value as 'line' | 'scatter');
   };

  const handleMaxPointsChange = (event: Event, newValue: number | number[]) => {
    setMaxPoints(newValue as number);
  };

                       // Smart timeline overview data loading with intelligent resolution
     const queryTimelineOverview = async () => {
       if (selectedTags.length === 0) return;

       try {
                   // Calculate smart timeline range - show broader context without overwhelming data
          const getSmartTimelineRange = () => {
            // Check if we already have timeline data and need to expand it
            let rangeStart = startTime;
            let rangeEnd = endTime;
            
            if (timelineData && timelineData.data.length > 0) {
              const timelineDataPoints = timelineData.data[0]?.data_points || [];
              if (timelineDataPoints.length > 0) {
                const currentTimelineStart = new Date(timelineDataPoints[0].timestamp);
                const currentTimelineEnd = new Date(timelineDataPoints[timelineDataPoints.length - 1].timestamp);
                
                // Expand range to include current view window plus buffer
                rangeStart = new Date(Math.min(
                  currentTimelineStart.getTime(),
                  viewStartTime.getTime(),
                  startTime.getTime()
                ));
                rangeEnd = new Date(Math.max(
                  currentTimelineEnd.getTime(), 
                  viewEndTime.getTime(),
                  endTime.getTime()
                ));
              }
            }
            
            const selectedDuration = rangeEnd.getTime() - rangeStart.getTime();
            const days = selectedDuration / (1000 * 60 * 60 * 24);
            
            // Smart extension based on current range
            let extensionMultiplier: number;
            if (days <= 1) extensionMultiplier = 5.0;      // 1 day → show 6 days total
            else if (days <= 7) extensionMultiplier = 3.0; // 1 week → show 1 month total
            else if (days <= 30) extensionMultiplier = 2.0; // 1 month → show 3 months total
            else if (days <= 90) extensionMultiplier = 1.5; // 3 months → show 6 months total
            else extensionMultiplier = 1.0;                // Longer periods → double range
            
            const extension = selectedDuration * extensionMultiplier;
            return {
              start: new Date(rangeStart.getTime() - extension),
              end: new Date(rangeEnd.getTime() + extension),
              extension: extension
            };
          };

         // Intelligent resolution selection for timeline - optimized for trend detection
         const getSmartTimelineResolution = (totalDuration: number) => {
           const hours = totalDuration / (1000 * 60 * 60);
           const days = hours / 24;
           
           // Target: 1000-2000 points maximum for smooth performance
           // But ensure we capture important variations and trends
           
           if (hours <= 6) return 'raw';        // Very short periods - show all data
           if (hours <= 48) return '1min';      // 2 days - minute data
           if (days <= 14) return '5min';       // 2 weeks - 5-minute data  
           if (days <= 60) return '15min';      // 2 months - 15-minute data
           if (days <= 180) return '1hour';     // 6 months - hourly data
           if (days <= 730) return '4hour';     // 2 years - 4-hour data (custom)
           return '1day';                       // Long periods - daily data
         };

         const timelineRange = getSmartTimelineRange();
         const timelineDuration = timelineRange.end.getTime() - timelineRange.start.getTime();
         const smartResolution = getSmartTimelineResolution(timelineDuration);
         
         // Dynamic max points based on resolution
         const getMaxPoints = (resolution: string) => {
           switch (resolution) {
             case 'raw': return 5000;      // Raw data needs more points
             case '1min': return 3000;     // Minute data
             case '5min': return 2000;     // 5-minute data
             case '15min': return 1500;    // 15-minute data
             case '1hour': return 1200;    // Hourly data
             case '4hour': return 800;     // 4-hour data
             case '1day': return 500;      // Daily data
             default: return 1000;
           }
         };

         console.log('📊 Smart timeline overview query:', {
           selectedRange: `${startTime.toISOString()} to ${endTime.toISOString()}`,
           timelineRange: `${timelineRange.start.toISOString()} to ${timelineRange.end.toISOString()}`,
           selectedDays: (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60 * 24),
           timelineDays: timelineDuration / (1000 * 60 * 60 * 24),
           smartResolution: smartResolution,
           maxPoints: getMaxPoints(smartResolution)
         });

         const params: HistorianQueryParams = {
           tags: selectedTags.map(tag => tag.tag_name),
           start_time: timelineRange.start.toISOString(),
           end_time: timelineRange.end.toISOString(),
           resolution: smartResolution,
           max_points: getMaxPoints(smartResolution),
         };

         const data = await scadaUtils.queryHistorian(params);
         console.log('📊 Smart timeline data received:', {
                   tags: data.data?.length || 0,
        totalPoints: data.data?.reduce((sum, tag) => sum + tag.data_points.length, 0) || 0,
           resolution: data.query_info?.resolution || 'unknown',
           tagsRequested: data.query_info?.tags_requested || 0,
           tagsFound: data.query_info?.tags_found || 0
         });
         setTimelineData(data);
       } catch (err) {
         console.warn('Failed to load smart timeline overview:', err);
       }
     };

       const queryHistorianData = async () => {
      if (selectedTags.length === 0) {
        setError('Please select at least one SCADA tag');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('🔍 Starting historian query...');
        const params: HistorianQueryParams = {
          tags: selectedTags.map(tag => tag.tag_name),
          start_time: viewStartTime.toISOString(),
          end_time: viewEndTime.toISOString(),
          resolution,
          max_points: maxPoints,
        };

        console.log('📊 Query params:', params);
        const data = await scadaUtils.queryHistorian(params);
        console.log('✅ Historian data received:', data);
        setHistorianData(data);
        
        // Only query timeline overview on initial load or if tags change
        if (showTimelineOverview && !timelineData) {
          await queryTimelineOverview();
        }
      } catch (err: any) {
        console.error('❌ Historian query failed:', err);
        setError(err.message || 'Failed to query historian data');
      } finally {
        setLoading(false);
      }
    };

  const exportToCSV = () => {
    if (!historianData || !historianData.data || !Array.isArray(historianData.data)) return;

    // Prepare data for CSV export
    const csvData: any[] = [];
    
                   // Create header row (values only)
      const headers = ['Timestamp'];
      historianData.data.forEach(tag => {
        headers.push(`${tag.tag_name} (${tag.engineering_unit || 'N/A'})`);
      });
      csvData.push(headers);

      // Collect all unique timestamps
      const timestamps = new Set<string>();
      historianData.data.forEach(tag => {
        tag.data_points.forEach(point => timestamps.add(point.timestamp));
      });

     // Sort timestamps
     const sortedTimestamps = Array.from(timestamps).sort();

     // Create data rows (values only)
     sortedTimestamps.forEach(timestamp => {
       const row = [timestamp];
       
       historianData.data.forEach(tag => {
         const point = tag.data_points.find(p => p.timestamp === timestamp);
         row.push(point && point.value !== null ? point.value.toString() : '');
       });
       
       csvData.push(row);
     });

    // Generate and download CSV
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `scada_historian_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    if (!historianData || !historianData.data || !Array.isArray(historianData.data)) return;

    const wb = XLSX.utils.book_new();
    
    // Create summary sheet
    const summaryData = [
      ['SCADA Historian Export'],
      ['Export Date:', new Date().toISOString()],
      ['Time Range:', `${startTime.toISOString()} to ${endTime.toISOString()}`],
      ['Resolution:', resolution],
              ['Total Tags:', historianData.data?.length || 0],
      [''],
      ['Tag Summary:'],
      ['Tag Name', 'Data Points', 'Min Value', 'Max Value', 'Avg Value']
    ];

         historianData.data.forEach(tag => {
       const values = tag.data_points.map(p => p.value).filter(v => v !== null) as number[];
       const minVal = values.length > 0 ? Math.min(...values) : 'N/A';
       const maxVal = values.length > 0 ? Math.max(...values) : 'N/A';
       const avgVal = values.length > 0 ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : 'N/A';
       
       summaryData.push([tag.tag_name, tag.data_points.length, minVal, maxVal, avgVal]);
     });

    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Create data sheet (same as CSV logic)
    const csvData: any[] = [];
         const headers = ['Timestamp'];
     historianData.data.forEach(tag => {
       headers.push(`${tag.tag_name} (${tag.engineering_unit || 'N/A'})`);
     });
     csvData.push(headers);

     const timestamps = new Set<string>();
     historianData.data.forEach(tag => {
       tag.data_points.forEach(point => timestamps.add(point.timestamp));
     });

     const sortedTimestamps = Array.from(timestamps).sort();

     sortedTimestamps.forEach(timestamp => {
       const row = [timestamp];
       historianData.data.forEach(tag => {
         const point = tag.data_points.find(p => p.timestamp === timestamp);
         row.push(point && point.value !== null ? point.value.toString() : '');
       });
       csvData.push(row);
     });

    const dataWs = XLSX.utils.aoa_to_sheet(csvData);
    XLSX.utils.book_append_sheet(wb, dataWs, 'Data');

    // Save file
    XLSX.writeFile(wb, `scada_historian_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

     // Prepare chart data with multi-axis support
   // FIXED: Color/Label offset issue when combining axes by units
   // - Axis colors are now consistent with dataset colors
   // - Tags sharing the same unit/axis get color variations of the axis color
   // - Legend is grouped by axis for better clarity
   const prepareChartData = (): ChartData<'line'> => {
     console.log('📈 Preparing chart data...', { historianData, chartType });
     
     if (!historianData || !historianData.data || !Array.isArray(historianData.data)) {
       console.log('⚠️ No historian data available or data is not an array');
       return { labels: [], datasets: [] };
     }

     // Group tags by engineering unit for axis assignment
     const unitGroups = new Map<string, HistorianTagData[]>();
     historianData.data.forEach(tag => {
       const unit = tag.engineering_unit || 'N/A';
       if (!unitGroups.has(unit)) {
         unitGroups.set(unit, []);
       }
       unitGroups.get(unit)!.push(tag);
     });

     console.log('📊 Unit groups:', Array.from(unitGroups.keys()));

     // Generate color palette for multiple tags
     const baseColors = [
       '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
       '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
     ];

     // Create unit to axis mapping from axis configurations and extract axis colors
     const unitToAxisId = new Map<string, string>();
     const unitToAxisColor = new Map<string, string>();
     Array.from(axisConfigs.values()).forEach((axisConfig: AxisConfig) => {
       unitToAxisId.set(axisConfig.unit, axisConfig.id);
       unitToAxisColor.set(axisConfig.unit, axisConfig.color);
     });

     // Create color variations for tags sharing the same axis
     const generateAxisColorVariation = (baseColor: string, index: number, totalForAxis: number): string => {
       if (totalForAxis === 1) return baseColor;
       
       // Convert hex to RGB
       const hex = baseColor.replace('#', '');
       const r = parseInt(hex.substr(0, 2), 16);
       const g = parseInt(hex.substr(2, 2), 16);
       const b = parseInt(hex.substr(4, 2), 16);
       
       // Create variations by adjusting brightness/saturation
       const variation = (index + 1) / (totalForAxis + 1); // 0.33, 0.5, 0.66 for 3 tags
       const factor = 0.6 + (variation * 0.8); // Range from 0.6 to 1.4
       
       const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
       const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
       const newB = Math.min(255, Math.max(0, Math.round(b * factor)));
       
       return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
     };

     // Pre-calculate color assignments per unit
     const unitColorAssignments = new Map<string, { baseColor: string; tags: HistorianTagData[] }>();
     Array.from(unitGroups.entries()).forEach(([unit, tags]) => {
       const axisColor = unitToAxisColor.get(unit) || baseColors[0];
       unitColorAssignments.set(unit, { baseColor: axisColor, tags });
     });

     const datasets = historianData.data.map((tag) => {
       const unit = tag.engineering_unit || 'N/A';
       const yAxisID = unitToAxisId.get(unit) || 'y';
       
       // Get color assignment for this unit
       const unitAssignment = unitColorAssignments.get(unit);
       const baseAxisColor = unitAssignment?.baseColor || baseColors[0];
       const tagsForThisUnit = unitAssignment?.tags || [tag];
       const tagIndexInUnit = tagsForThisUnit.findIndex(t => t.tag_name === tag.tag_name);
       
       // Generate color variation based on position within the unit group
       const tagColor = generateAxisColorVariation(baseAxisColor, tagIndexInUnit, tagsForThisUnit.length);
       
       console.log(`📊 Processing tag ${tag.tag_name} (${unit}) on axis ${yAxisID} with color ${tagColor}`);
       
       const dataset = {
         label: `${tag.tag_name} (${unit})`,
         data: tag.data_points.map(point => ({
           x: new Date(point.timestamp).getTime(),
           y: point.value,
         })),
         borderColor: tagColor,
         backgroundColor: tagColor + '20',
         borderWidth: 2,
         pointRadius: chartType === 'scatter' ? 3 : 1,
         pointHoverRadius: 5,
         tension: chartType === 'line' ? 0.1 : 0,
         spanGaps: true,
         yAxisID: yAxisID,
         // Enable stepped chart for boolean tags (digital signals should not be interpolated)
         stepped: tag.data_type === 'BOOL' ? ('before' as const) : false,
       };
       
       return dataset;
     });

     console.log('✅ Chart data prepared:', { datasets: datasets.length, axes: unitGroups.size });
     return {
       datasets,
     };
   };

           // Generate dynamic chart options with multi-axis support
    const generateChartOptions = (): ChartOptions<'line'> => {
      if (!historianData) {
        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: { x: { type: 'time' } }
        };
      }

             // Calculate optimal time unit based on data range
       const getOptimalTimeUnit = () => {
         const duration = viewEndTime.getTime() - viewStartTime.getTime();
         const hours = duration / (1000 * 60 * 60);
         const days = hours / 24;
         
         if (hours <= 2) return 'minute';
         if (hours <= 24) return 'hour';
         if (days <= 7) return 'day';
         if (days <= 60) return 'week';
         if (days <= 365) return 'month';
         return 'year';
       };

       const timeUnit = getOptimalTimeUnit();
       
       // Generate Y-axis configurations from axisConfigs
       const scales: any = {
         x: {
           type: 'time',
           time: {
             unit: timeUnit,
             // Explicitly tell Chart.js to treat timestamps as UTC and convert to local time
             round: false,
             displayFormats: {
               millisecond: 'HH:mm:ss.SSS',
               second: 'HH:mm:ss',
               minute: 'HH:mm',
               hour: 'MMM dd HH:mm',
               day: 'MMM dd',
               week: 'MMM dd',
               month: 'MMM yyyy',
               quarter: 'MMM yyyy',
               year: 'yyyy',
             },
             tooltipFormat: timeUnit === 'minute' || timeUnit === 'hour' ? 'MMM dd, yyyy HH:mm:ss' : 'MMM dd, yyyy',
           },
           title: {
             display: true,
             text: `Time (${Intl.DateTimeFormat().resolvedOptions().timeZone})`,
           },
           min: viewStartTime.getTime(),
           max: viewEndTime.getTime(),
         }
       };

      // Create scales from axis configurations
      Array.from(axisConfigs.values()).forEach((axisConfig: AxisConfig, index: number) => {
        scales[axisConfig.id] = {
          type: 'linear',
          position: axisConfig.position,
          title: {
            display: true,
            text: axisConfig.unit,
            color: axisConfig.color,
          },
          ticks: {
            color: axisConfig.color,
          },
          beginAtZero: axisConfig.scaling === 'zero',
          min: axisConfig.scaling === 'custom' ? axisConfig.min : undefined,
          max: axisConfig.scaling === 'custom' ? axisConfig.max : undefined,
          grid: {
            drawOnChartArea: index === 0, // Only show grid for first axis
          },
        };
      });

     return {
       responsive: true,
       maintainAspectRatio: false,
       plugins: {
         title: {
           display: true,
           text: 'SCADA Historian Data - Multi-Axis View',
           font: { size: 16 },
         },
         legend: {
           display: true,
           position: 'top',
           labels: {
             generateLabels: (chart) => {
               const datasets = chart.data.datasets;
               if (!datasets || datasets.length === 0) return [];
               
               // Group datasets by axis
               const axisGroups = new Map<string, any[]>();
               datasets.forEach((dataset, index) => {
                 const yAxisID = (dataset as any).yAxisID || 'y';
                 const axisConfig = Array.from(axisConfigs.values()).find((config: AxisConfig) => config.id === yAxisID);
                 const axisLabel = axisConfig ? `${axisConfig.unit} (${axisConfig.position})` : yAxisID;
                 
                 if (!axisGroups.has(axisLabel)) {
                   axisGroups.set(axisLabel, []);
                 }
                 
                 axisGroups.get(axisLabel)!.push({
                   text: dataset.label,
                   fillStyle: (dataset as any).borderColor,
                   strokeStyle: (dataset as any).borderColor,
                   lineWidth: 2,
                   hidden: chart.isDatasetVisible(index) === false,
                   datasetIndex: index,
                   pointStyle: 'line',
                 });
               });
               
               // Flatten groups with separators
               const legendItems: any[] = [];
               Array.from(axisGroups.entries()).forEach(([axisLabel, items], groupIndex) => {
                 // Get the axis color from the first item in this group
                 const firstItem = items[0];
                 const yAxisID = (firstItem && chart.data.datasets[firstItem.datasetIndex]) ? 
                   (chart.data.datasets[firstItem.datasetIndex] as any).yAxisID || 'y' : 'y';
                 const axisConfig = Array.from(axisConfigs.values()).find((config: AxisConfig) => config.id === yAxisID);
                 const axisColor = axisConfig?.color || '#666';
                 
                 // Add axis group header
                 if (groupIndex > 0) {
                   legendItems.push({
                     text: '',
                     fillStyle: 'transparent',
                     strokeStyle: 'transparent',
                     lineWidth: 0,
                     hidden: false,
                     datasetIndex: -1,
                     pointStyle: 'line',
                   });
                 }
                 
                 legendItems.push({
                   text: `${axisLabel}:`,
                   fillStyle: axisColor,
                   strokeStyle: axisColor,
                   lineWidth: 2,
                   hidden: false,
                   datasetIndex: -1,
                   pointStyle: 'line',
                   font: {
                     weight: 'bold',
                     size: 11
                   }
                 });
                 
                 // Add items for this axis
                 items.forEach(item => {
                   legendItems.push({
                     ...item,
                     text: `  ${item.text}`, // Indent tag names
                   });
                 });
               });
               
               return legendItems;
             }
           },
           onClick: (e, legendItem, legend) => {
             if (legendItem.datasetIndex >= 0) {
               const chart = legend.chart;
               const index = legendItem.datasetIndex;
               const meta = chart.getDatasetMeta(index);
               meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
               chart.update();
             }
           },
         },
         tooltip: {
           mode: 'index',
           intersect: false,
           callbacks: {
             title: (tooltipItems) => {
               if (tooltipItems.length > 0) {
                 return new Date(tooltipItems[0].parsed.x).toLocaleString();
               }
               return '';
             },
             label: (context) => {
               const label = context.dataset.label || '';
               const value = context.parsed.y;
               return `${label}: ${value !== null ? value.toFixed(3) : 'null'}`;
             }
           },
         },
         zoom: {
           pan: {
             enabled: true,
             mode: 'x',
           },
           zoom: {
             wheel: {
               enabled: true,
             },
             pinch: {
               enabled: true,
             },
             mode: 'x',
           },
         },
       },
       scales,
       interaction: {
         mode: 'nearest',
         axis: 'x',
         intersect: false,
       },
     };
   };

   const chartOptions = generateChartOptions();

  const chartData = prepareChartData();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <TimelineIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            SCADA Historian
          </Typography>
        </Box>

        {/* Query Configuration */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Query Configuration
            </Typography>
            
            <Grid container spacing={3}>
                             {/* Tag Selection */}
               <Grid item xs={12}>
                 <Autocomplete
                   multiple
                   options={availableTags}
                   getOptionLabel={(option) => `${option.tag_name} (${option.description || 'No description'}) - ${option.engineering_unit || 'N/A'}`}
                   value={selectedTags}
                   onChange={handleTagChange}
                   loading={tagsLoading}
                   filterOptions={(options, { inputValue }) => {
                     const filtered = options.filter(option => 
                       option.tag_name.toLowerCase().includes(inputValue.toLowerCase()) ||
                       (option.description && option.description.toLowerCase().includes(inputValue.toLowerCase())) ||
                       (option.engineering_unit && option.engineering_unit.toLowerCase().includes(inputValue.toLowerCase()))
                     );
                     return filtered.slice(0, 200); // Limit to 200 results for performance
                   }}
                   noOptionsText="No tags found - try searching by tag name, description, or unit"
                                       renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {option.tag_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description || 'No description'} • {option.engineering_unit || 'No unit'}
                            </Typography>
                            {option.equipment && (
                              <Typography variant="caption" color="text.secondary">
                                Equipment: {option.equipment}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    }}
                   renderTags={(value, getTagProps) =>
                     value.map((option, index) => (
                       <Chip
                         variant="outlined"
                         label={`${option.tag_name} (${option.engineering_unit || 'N/A'})`}
                         {...getTagProps({ index })}
                         key={option.id}
                         size="small"
                       />
                     ))
                   }
                   renderInput={(params) => (
                     <TextField
                       {...params}
                       label="Search & Select SCADA Tags"
                       placeholder="Type to search by name, description, or unit..."
                       variant="outlined"
                       helperText={`${availableTags.length} tags available • Type to search by name, description, or unit • Showing top 200 matches for performance`}
                       InputProps={{
                         ...params.InputProps,
                         endAdornment: (
                           <>
                             {tagsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                             {params.InputProps.endAdornment}
                           </>
                         ),
                       }}
                     />
                   )}
                 />
               </Grid>

                             {/* Quick Time Range Selection */}
               <Grid item xs={12}>
                 <Typography variant="subtitle2" gutterBottom>
                   Quick Time Range
                 </Typography>
                 <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                   {[
                     { label: '-1h', hours: 1 },
                     { label: '-6h', hours: 6 },
                     { label: '-12h', hours: 12 },
                     { label: '-24h', hours: 24 },
                     { label: '-3d', hours: 72 },
                     { label: '-7d', hours: 168 },
                     { label: '-30d', hours: 720 },
                   ].map((preset) => (
                     <Button
                       key={preset.label}
                       size="small"
                       variant="outlined"
                                               onClick={() => {
                          const now = new Date();
                          const start = new Date(now.getTime() - preset.hours * 60 * 60 * 1000);
                          setStartTime(start);
                          setEndTime(now);
                          setViewStartTime(start);
                          setViewEndTime(now);
                          setTimelineData(null); // Force timeline reload
                        }}
                     >
                       {preset.label}
                     </Button>
                   ))}
                   <Button
                     size="small"
                     variant="outlined"
                                           onClick={() => {
                        const now = new Date();
                        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                        setStartTime(startOfMonth);
                        setEndTime(now);
                        setViewStartTime(startOfMonth);
                        setViewEndTime(now);
                        setTimelineData(null);
                      }}
                   >
                     This Month
                   </Button>
                   <Button
                     size="small"
                     variant="outlined"
                                           onClick={() => {
                        const now = new Date();
                        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
                        setStartTime(startOfLastMonth);
                        setEndTime(endOfLastMonth);
                        setViewStartTime(startOfLastMonth);
                        setViewEndTime(endOfLastMonth);
                        setTimelineData(null);
                      }}
                   >
                     Last Month
                   </Button>
                 </Box>
               </Grid>

               {/* Precise Time Range */}
               <Grid item xs={12} md={6}>
                 <DateTimePicker
                   label="Start Time"
                   value={startTime}
                   onChange={(newValue) => newValue && setStartTime(newValue)}
                   slotProps={{ 
                     textField: { 
                       fullWidth: true,
                       helperText: 'Set precise start time'
                     } 
                   }}
                 />
               </Grid>
               <Grid item xs={12} md={6}>
                 <DateTimePicker
                   label="End Time"
                   value={endTime}
                   onChange={(newValue) => newValue && setEndTime(newValue)}
                   slotProps={{ 
                     textField: { 
                       fullWidth: true,
                       helperText: 'Set precise end time'
                     } 
                   }}
                 />
               </Grid>

              {/* Resolution and Max Points */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Data Resolution</InputLabel>
                  <Select
                    value={resolution}
                    label="Data Resolution"
                    onChange={handleResolutionChange}
                  >
                    {RESOLUTION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Max Data Points: {maxPoints}</Typography>
                <Slider
                  value={maxPoints}
                  onChange={handleMaxPointsChange}
                  min={100}
                  max={10000}
                  step={100}
                  marks={[
                    { value: 100, label: '100' },
                    { value: 1000, label: '1K' },
                    { value: 5000, label: '5K' },
                    { value: 10000, label: '10K' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>
            </Grid>

            {/* Query Button */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={queryHistorianData}
                disabled={loading || selectedTags.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                {loading ? 'Querying...' : 'Query Data'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={loadAvailableTags}
                disabled={tagsLoading}
                startIcon={<RefreshIcon />}
              >
                Refresh Tags
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Chart Configuration and Display */}
        {historianData && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Historical Data Chart
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={exportToCSV}
                    startIcon={<DownloadIcon />}
                  >
                    CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={exportToExcel}
                    startIcon={<DownloadIcon />}
                  >
                    Excel
                  </Button>
                </Box>
              </Box>

                             {/* Chart Settings */}
               <Grid container spacing={2} sx={{ mb: 3 }}>
                 <Grid item xs={12} md={6}>
                   <FormControl fullWidth size="small">
                     <InputLabel>Chart Type</InputLabel>
                     <Select
                       value={chartType}
                       label="Chart Type"
                       onChange={handleChartTypeChange}
                     >
                       {CHART_TYPE_OPTIONS.map((option) => (
                         <MenuItem key={option.value} value={option.value}>
                           {option.label}
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                 </Grid>
               </Grid>

               {/* Individual Axis Configuration */}
               {axisConfigs.size > 0 && (
                 <Box sx={{ mb: 3 }}>
                   <Typography variant="subtitle1" gutterBottom>
                     Axis Configuration ({axisConfigs.size} axes)
                   </Typography>
                   {Array.from(axisConfigs.values()).map((axisConfig, index) => (
                     <Card key={axisConfig.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                       <Grid container spacing={2} alignItems="center">
                         <Grid item xs={12} md={2}>
                           <Typography variant="body2" fontWeight="bold" sx={{ color: axisConfig.color }}>
                             {axisConfig.unit} ({axisConfig.position})
                           </Typography>
                           <Typography variant="caption" color="text.secondary">
                             Axis {axisConfig.id}
                           </Typography>
                         </Grid>
                         <Grid item xs={12} md={3}>
                           <FormControl fullWidth size="small">
                             <InputLabel>Scaling</InputLabel>
                             <Select
                               value={axisConfig.scaling}
                               label="Scaling"
                               onChange={(e) => {
                                 const newConfigs = new Map(axisConfigs);
                                 newConfigs.set(axisConfig.id, {
                                   ...axisConfig,
                                   scaling: e.target.value as 'auto' | 'zero' | 'custom'
                                 });
                                 setAxisConfigs(newConfigs);
                               }}
                             >
                               {Y_AXIS_OPTIONS.map((option) => (
                                 <MenuItem key={option.value} value={option.value}>
                                   {option.label}
                                 </MenuItem>
                               ))}
                             </Select>
                           </FormControl>
                         </Grid>
                         {axisConfig.scaling === 'custom' && (
                           <>
                                                           <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Min"
                                  type="number"
                                  value={axisConfig.min !== undefined ? axisConfig.min : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const newConfigs = new Map(axisConfigs);
                                    newConfigs.set(axisConfig.id, {
                                      ...axisConfig,
                                      min: value === '' ? undefined : Number(value)
                                    });
                                    setAxisConfigs(newConfigs);
                                  }}
                                  inputProps={{
                                    step: "any"
                                  }}
                                  placeholder="Auto"
                                />
                              </Grid>
                              <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Max"
                                  type="number"
                                  value={axisConfig.max !== undefined ? axisConfig.max : ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const newConfigs = new Map(axisConfigs);
                                    newConfigs.set(axisConfig.id, {
                                      ...axisConfig,
                                      max: value === '' ? undefined : Number(value)
                                    });
                                    setAxisConfigs(newConfigs);
                                  }}
                                  inputProps={{
                                    step: "any"
                                  }}
                                  placeholder="Auto"
                                />
                              </Grid>
                           </>
                         )}
                         <Grid item xs={12} md={3}>
                           <FormControl fullWidth size="small">
                             <InputLabel>Position</InputLabel>
                             <Select
                               value={axisConfig.position}
                               label="Position"
                               onChange={(e) => {
                                 const newConfigs = new Map(axisConfigs);
                                 newConfigs.set(axisConfig.id, {
                                   ...axisConfig,
                                   position: e.target.value as 'left' | 'right'
                                 });
                                 setAxisConfigs(newConfigs);
                               }}
                             >
                               <MenuItem value="left">Left</MenuItem>
                               <MenuItem value="right">Right</MenuItem>
                             </Select>
                           </FormControl>
                         </Grid>
                       </Grid>
                     </Card>
                   ))}
                 </Box>
               )}

                                                           {/* Timeline Overview */}
                                                  <Box sx={{ mb: 2 }}>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                     <Typography variant="subtitle2">Timeline Overview</Typography>
                     <FormControlLabel
                       control={
                         <Switch
                           checked={showTimelineOverview}
                           onChange={(e) => {
                             const newValue = e.target.checked;
                             setShowTimelineOverview(newValue);
                             // If turning timeline back on and we have selected tags but no timeline data, reload it
                             if (newValue && selectedTags.length > 0 && !timelineData) {
                               setTimeout(() => queryTimelineOverview(), 100);
                             }
                           }}
                           size="small"
                         />
                       }
                       label="Show Timeline"
                     />
                   </Box>
                   
                                       {showTimelineOverview && (
                      <Box sx={{ position: 'relative' }}>
                        {/* Real-time update indicator */}
                        {(isDragging || isResizing) && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: -25, 
                            right: 0, 
                            zIndex: 20,
                            backgroundColor: 'rgba(25, 118, 210, 0.9)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <CircularProgress size={12} color="inherit" />
                            Updating chart...
                          </Box>
                        )}
                                                 <Paper sx={{ p: 1, height: 120, position: 'relative', cursor: 'crosshair' }} data-timeline-container>
                        {timelineData ? (
                          <>
                            {/* High-level time scale header */}
                            <Box sx={{ 
                              height: 20, 
                              borderBottom: '1px solid rgba(0,0,0,0.1)', 
                              mb: 1, 
                              position: 'relative',
                              fontSize: '0.7rem',
                              color: 'text.primary'
                            }}>
                              {(() => {
                                // Calculate timeline bounds
                                const timelineDataPoints = timelineData.data[0]?.data_points || [];
                                if (timelineDataPoints.length === 0) return null;
                                
                                const timelineStart = new Date(timelineDataPoints[0].timestamp);
                                const timelineEnd = new Date(timelineDataPoints[timelineDataPoints.length - 1].timestamp);
                                const timelineDuration = timelineEnd.getTime() - timelineStart.getTime();
                                const days = timelineDuration / (1000 * 60 * 60 * 24);
                                
                                // Determine suitable time unit and interval
                                let unit: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
                                let interval: number; // in milliseconds
                                let formatString: string;
                                
                                if (days <= 2) {
                                  unit = 'hour';
                                  interval = 6 * 60 * 60 * 1000; // 6 hours
                                  formatString = 'HH:mm';
                                } else if (days <= 14) {
                                  unit = 'day';
                                  interval = 24 * 60 * 60 * 1000; // 1 day
                                  formatString = 'MMM dd';
                                } else if (days <= 60) {
                                  unit = 'week';
                                  interval = 7 * 24 * 60 * 60 * 1000; // 1 week
                                  formatString = 'MMM dd';
                                } else if (days <= 365) {
                                  unit = 'month';
                                  interval = 30 * 24 * 60 * 60 * 1000; // 1 month (approx)
                                  formatString = 'MMM yyyy';
                                } else if (days <= 1095) { // 3 years
                                  unit = 'quarter';
                                  interval = 90 * 24 * 60 * 60 * 1000; // 3 months
                                  formatString = 'MMM yyyy';
                                } else {
                                  unit = 'year';
                                  interval = 365 * 24 * 60 * 60 * 1000; // 1 year
                                  formatString = 'yyyy';
                                }
                                
                                // Generate tick marks
                                const ticks = [];
                                let currentTime = new Date(Math.ceil(timelineStart.getTime() / interval) * interval);
                                
                                while (currentTime.getTime() <= timelineEnd.getTime() && ticks.length < 12) {
                                  const position = ((currentTime.getTime() - timelineStart.getTime()) / timelineDuration) * 100;
                                  // Filter out ticks too close to start (< 8%) or end (> 92%) to avoid overlapping with bold start/end dates
                                  if (position >= 8 && position <= 92) {
                                    ticks.push({
                                      position,
                                      time: new Date(currentTime),
                                      label: currentTime.toLocaleDateString('en-US', 
                                        unit === 'hour' ? { hour: 'numeric', hour12: false } :
                                        unit === 'day' || unit === 'week' ? { month: 'short', day: 'numeric' } :
                                        unit === 'month' || unit === 'quarter' ? { month: 'short', year: 'numeric' } :
                                        { year: 'numeric' }
                                      )
                                    });
                                  }
                                  currentTime = new Date(currentTime.getTime() + interval);
                                }
                                
                                return (
                                  <>
                                    {/* Start date */}
                                    <Box sx={{ 
                                      position: 'absolute', 
                                      left: 0, 
                                      top: 0,
                                      fontWeight: 'bold'
                                    }}>
                                      {timelineStart.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: days > 365 ? 'numeric' : undefined
                                      })}
                                    </Box>
                                    
                                    {/* End date */}
                                    <Box sx={{ 
                                      position: 'absolute', 
                                      right: 0, 
                                      top: 0,
                                      fontWeight: 'bold'
                                    }}>
                                      {timelineEnd.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: days > 365 ? 'numeric' : undefined
                                      })}
                                    </Box>
                                    
                                    {/* Time unit markers */}
                                    {ticks.map((tick, index) => (
                                      <Box key={index}>
                                        {/* Vertical line */}
                                        <Box sx={{
                                          position: 'absolute',
                                          left: `${tick.position}%`,
                                          top: 12,
                                          width: '1px',
                                          height: 8,
                                          backgroundColor: 'rgba(0,0,0,0.2)',
                                          transform: 'translateX(-50%)'
                                        }} />
                                        {/* Label */}
                                        <Box sx={{
                                          position: 'absolute',
                                          left: `${tick.position}%`,
                                          top: 2,
                                          transform: 'translateX(-50%)',
                                          fontSize: '0.6rem',
                                          color: 'text.secondary',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          {tick.label}
                                        </Box>
                                      </Box>
                                    ))}
                                  </>
                                );
                              })()}
                            </Box>
                            
                            {/* Chart area */}
                            <Box sx={{ height: 80 }}>
                              <Line 
                                data={{
                                  datasets: timelineData.data.map((tag, index) => ({
                                 label: tag.tag_name,
                                 data: tag.data_points.map(point => ({
                                   x: point.timestamp,
                                   y: point.value,
                                 })),
                                 borderColor: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'][index % 5],
                                 backgroundColor: 'transparent',
                                 borderWidth: 1,
                                 pointRadius: 0,
                                 tension: 0.1,
                                 // Enable stepped chart for boolean tags in timeline too
                                 stepped: tag.data_type === 'BOOL' ? ('before' as const) : false,
                               }))
                             }}
                                                                                                             options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: false },
                                  tooltip: { enabled: false },
                                },
                                scales: {
                                  x: {
                                    type: 'time',
                                    display: false, // Hide x-axis since we have custom time scale above
                                  },
                                  y: {
                                    display: false,
                                  },
                                },
                                elements: {
                                  point: { radius: 0 },
                                },
                                onHover: () => {},
                                onClick: (event, elements, chart) => {
                                  if (chart.scales.x && event.native) {
                                    const rect = chart.canvas.getBoundingClientRect();
                                    const nativeEvent = event.native as MouseEvent;
                                    const x = nativeEvent.clientX - rect.left;
                                    const dataX = chart.scales.x.getValueForPixel(x);
                                    if (dataX) {
                                      const clickedTime = new Date(dataX);
                                      const duration = viewEndTime.getTime() - viewStartTime.getTime();
                                      const newStart = new Date(clickedTime.getTime() - duration / 2);
                                      const newEnd = new Date(clickedTime.getTime() + duration / 2);
                                      console.log('📊 Timeline clicked:', {
                                        clickedTime: clickedTime.toISOString(),
                                        newViewRange: `${newStart.toISOString()} to ${newEnd.toISOString()}`
                                      });
                                      setViewStartTime(newStart);
                                      setViewEndTime(newEnd);
                                      // Re-query main chart with new view range
                                      setTimeout(() => {
                                        // Use a timeout to ensure state updates first
                                        queryHistorianData();
                                      }, 100);
                                    }
                                  }
                                },
                              }}
                              />
                            </Box>
                          </>
                         ) : (
                           <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                             <Typography variant="body2" color="text.secondary">
                               Timeline loading...
                             </Typography>
                           </Box>
                         )}
                         
                         {/* Draggable & Resizable Selection Window Overlay (only show if we have timeline data) */}
                         {timelineData && (() => {
                            // Calculate smart timeline bounds based on actual timeline data range
                            // Use the actual data range from timeline query for accurate positioning
                            const timelineDataPoints = timelineData.data[0]?.data_points || [];
                            let timelineStart: number, timelineEnd: number;
                            
                            if (timelineDataPoints.length > 0) {
                              // Use actual data range from timeline
                              timelineStart = new Date(timelineDataPoints[0].timestamp).getTime();
                              timelineEnd = new Date(timelineDataPoints[timelineDataPoints.length - 1].timestamp).getTime();
                            } else {
                              // Fallback to calculated range if no data
                              const selectedDuration = endTime.getTime() - startTime.getTime();
                              const days = selectedDuration / (1000 * 60 * 60 * 24);
                              let extensionMultiplier = days <= 1 ? 5.0 : days <= 7 ? 3.0 : days <= 30 ? 2.0 : days <= 90 ? 1.5 : 1.0;
                              const extension = selectedDuration * extensionMultiplier;
                              timelineStart = startTime.getTime() - extension;
                              timelineEnd = endTime.getTime() + extension;
                            }
                            
                            const timelineTotal = timelineEnd - timelineStart;
                           
                           // Calculate current view window position within the timeline
                           const viewStart = Math.max(0, (viewStartTime.getTime() - timelineStart) / timelineTotal);
                           const viewEnd = Math.min(1, (viewEndTime.getTime() - timelineStart) / timelineTotal);
                           const viewWidth = viewEnd - viewStart;
                           
                           // Drag handlers for moving the window
                           const handleDragMouseDown = (e: React.MouseEvent) => {
                             e.stopPropagation();
                             setIsDragging(true);
                             setIsUserInteracting(true);
                             setDragStartX(e.clientX);
                             setDragStartViewStart(viewStartTime);
                             setDragStartViewEnd(viewEndTime);
                           };
                           
                           // Resize handlers for left and right edges
                           const handleResizeMouseDown = (e: React.MouseEvent, handle: 'left' | 'right') => {
                             e.stopPropagation();
                             setIsResizing(true);
                             setIsUserInteracting(true);
                             setResizeHandle(handle);
                             setDragStartX(e.clientX);
                             setDragStartViewStart(viewStartTime);
                             setDragStartViewEnd(viewEndTime);
                           };
                           
                                                       // Event listeners are managed by the top-level useEffect
                           
                           const isActive = isDragging || isResizing;
                           
                           return (
                             <Box
                               sx={{
                                 position: 'absolute',
                                 top: 8,
                                 bottom: 8,
                                 left: `${viewStart * 100}%`,
                                 width: `${viewWidth * 100}%`,
                                 backgroundColor: isActive ? 'rgba(25, 118, 210, 0.4)' : 'rgba(25, 118, 210, 0.2)',
                                 border: '2px solid #1976d2',
                                 borderRadius: 1,
                                 cursor: isDragging ? 'grabbing' : 'grab',
                                 pointerEvents: 'auto',
                                 '&:hover': {
                                   backgroundColor: 'rgba(25, 118, 210, 0.3)',
                                 }
                               }}
                               onMouseDown={handleDragMouseDown}
                             >
                               {/* Left resize handle */}
                               <Box
                                 sx={{
                                   position: 'absolute',
                                   left: -4,
                                   top: 0,
                                   bottom: 0,
                                   width: 8,
                                   cursor: 'ew-resize',
                                   backgroundColor: 'transparent',
                                   '&:hover': {
                                     backgroundColor: 'rgba(25, 118, 210, 0.5)',
                                   },
                                   zIndex: 10,
                                 }}
                                 onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
                               />
                               
                               {/* Right resize handle */}
                               <Box
                                 sx={{
                                   position: 'absolute',
                                   right: -4,
                                   top: 0,
                                   bottom: 0,
                                   width: 8,
                                   cursor: 'ew-resize',
                                   backgroundColor: 'transparent',
                                   '&:hover': {
                                     backgroundColor: 'rgba(25, 118, 210, 0.5)',
                                   },
                                   zIndex: 10,
                                 }}
                                 onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
                               />
                               
                               {/* Visual indicators for resize handles */}
                               <Box
                                 sx={{
                                   position: 'absolute',
                                   left: 2,
                                   top: '50%',
                                   transform: 'translateY(-50%)',
                                   width: 3,
                                   height: 20,
                                   backgroundColor: '#1976d2',
                                   borderRadius: '2px',
                                   opacity: 0.8,
                                 }}
                               />
                               <Box
                                 sx={{
                                   position: 'absolute',
                                   right: 2,
                                   top: '50%',
                                   transform: 'translateY(-50%)',
                                   width: 3,
                                   height: 20,
                                   backgroundColor: '#1976d2',
                                   borderRadius: '2px',
                                   opacity: 0.8,
                                 }}
                               />
                             </Box>
                                                                                     );
                           })()}
                                                                                           </Paper>
                        </Box>
                      )}
                    </Box>

                                             {/* Chart */}
                 <Paper sx={{ p: 2, height: 500 }}>
                   {chartData.datasets.length > 0 ? (
                     <Line data={chartData} options={chartOptions} />
                   ) : (
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                       <Typography>No chart data available</Typography>
                     </Box>
                   )}
                 </Paper>

                 {/* Navigation Controls */}
                 {historianData && (
                   <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                                           <Tooltip title="Pan Left">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const duration = viewEndTime.getTime() - viewStartTime.getTime();
                            const panAmount = duration * 0.25; // Pan by 25% of current view
                            const newStart = new Date(viewStartTime.getTime() - panAmount);
                            const newEnd = new Date(viewEndTime.getTime() - panAmount);
                            setViewStartTime(newStart);
                            setViewEndTime(newEnd);
                            // Re-query with new view range
                            setTimeout(() => queryHistorianData(), 100);
                          }}
                        >
                          ←
                        </IconButton>
                      </Tooltip>
                                            <Tooltip title="Zoom In">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const center = (viewStartTime.getTime() + viewEndTime.getTime()) / 2;
                            const duration = (viewEndTime.getTime() - viewStartTime.getTime()) * 0.5; // Zoom in 50%
                            setViewStartTime(new Date(center - duration / 2));
                            setViewEndTime(new Date(center + duration / 2));
                            setTimeout(() => queryHistorianData(), 100);
                          }}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset View">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setViewStartTime(startTime);
                            setViewEndTime(endTime);
                            setTimeout(() => queryHistorianData(), 100);
                          }}
                        >
                          🏠
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zoom Out">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const center = (viewStartTime.getTime() + viewEndTime.getTime()) / 2;
                            const duration = (viewEndTime.getTime() - viewStartTime.getTime()) * 2; // Zoom out 2x
                            setViewStartTime(new Date(center - duration / 2));
                            setViewEndTime(new Date(center + duration / 2));
                            setTimeout(() => queryHistorianData(), 100);
                          }}
                        >
                          <ZoomOutIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Pan Right">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const duration = viewEndTime.getTime() - viewStartTime.getTime();
                            const panAmount = duration * 0.25; // Pan by 25% of current view
                            setViewStartTime(new Date(viewStartTime.getTime() + panAmount));
                            setViewEndTime(new Date(viewEndTime.getTime() + panAmount));
                            setTimeout(() => queryHistorianData(), 100);
                          }}
                        >
                          →
                        </IconButton>
                      </Tooltip>
                   </Box>
                 )}

              {/* Data Summary */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Query Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Time Range
                    </Typography>
                    <Typography variant="body1">
                      {historianData.query_info.start_time} to {historianData.query_info.end_time}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Resolution
                    </Typography>
                    <Typography variant="body1">
                      {historianData.query_info.resolution}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Data Points
                    </Typography>
                                         <Typography variant="body1">
                       {historianData.data.reduce((sum, tag) => sum + tag.data_points.length, 0)}
                     </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Tags Found
                    </Typography>
                    <Typography variant="body1">
                      {historianData.query_info.tags_found} / {historianData.query_info.tags_requested}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Placeholder when no data */}
        {!historianData && !loading && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <TimelineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Data to Display
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select SCADA tags and configure your query to view historical data.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SCADAHistorian; 